import { Card } from '../components/Card';

export function Terms() {
  return (
    <div className="mx-auto max-w-md px-4 pt-4 pb-24 space-y-4">
      <h1 className="font-display text-3xl uppercase tracking-wide">Terms</h1>
      <div className="font-mono text-[10px] text-text-faint">Effective 19 May 2026</div>

      <Card>
        <p className="text-sm text-text-base leading-relaxed">
          These terms are a contract between you and PunditXI (operated by James Cox, a sole
          trader based in England). By using the PunditXI Telegram Mini App or chatting with
          <span className="font-mono text-trophy-gold"> @PunditXIBot</span>, you agree to these
          terms. Read them carefully.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">1. What PunditXI is</h2>
        <p className="text-sm text-text-dim leading-relaxed">
          PunditXI is a football prediction game. You make picks on upcoming matches and earn
          points for correct picks. The headline feature is a paid bracket entry for the 2026
          FIFA World Cup. Daily fixture predictions are free; optional Stars-based items unlock
          features (bracket entry, prediction boost, insurance, tipster packs, re-locks, premium
          subscription, sponsored leagues).
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">2. Eligibility</h2>
        <ul className="space-y-2 text-sm text-text-dim list-disc list-inside">
          <li>You must be 18 or older.</li>
          <li>You must have a valid Telegram account in good standing.</li>
          <li>You must not be barred from using digital services in your jurisdiction.</li>
        </ul>
        <p className="text-sm text-text-dim leading-relaxed mt-2">
          PunditXI is a skill-based prediction game played for points, not for money or
          gambling. Stars purchases unlock features within the app; they do not produce cash
          winnings.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">3. Your account</h2>
        <p className="text-sm text-text-dim leading-relaxed">
          Your PunditXI account is tied to your Telegram identity. One account per Telegram user.
          You are responsible for everything that happens on your account. Don't share your
          Telegram session with anyone you wouldn't trust with your messages.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">4. Payments — Telegram Stars</h2>
        <p className="text-sm text-text-dim leading-relaxed mb-2">
          All payments are processed by Telegram in Stars (XTR), Telegram's in-platform currency.
          PunditXI receives a payment confirmation from Telegram and applies the corresponding
          in-app effect. Stars are bought from Telegram, not from us.
        </p>
        <p className="text-sm text-text-dim leading-relaxed">
          Prices are displayed in Stars before you confirm a purchase. Telegram may show a local
          currency equivalent based on your region's exchange rate.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">5. Subscriptions</h2>
        <p className="text-sm text-text-dim leading-relaxed mb-2">
          PunditXI Premium is a recurring 30-day subscription at 200 ⭐ per period. Telegram
          renews it automatically every 30 days until you cancel. Cancel at any time via Telegram
          → Settings → Subscriptions. Cancellation takes effect at the end of the current period;
          you keep Premium benefits until then.
        </p>
        <p className="text-sm text-text-dim leading-relaxed">
          We do not pro-rate refunds for mid-period cancellations.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">6. Refunds</h2>
        <p className="text-sm text-text-dim leading-relaxed mb-2">
          Telegram does not currently support programmatic refunds back to your Telegram Stars
          balance. Where we owe a refund, we issue it as{' '}
          <strong className="text-text-base">in-app credit</strong> at 1:1 parity with the
          original Stars amount. In-app credit is spendable on any future PunditXI Stars item.
          It does not expire and cannot be converted back to Telegram Stars or to cash.
        </p>
        <p className="text-sm text-text-dim leading-relaxed mb-2">
          Refunds are issued automatically in these scenarios:
        </p>
        <ul className="space-y-2 text-sm text-text-dim list-disc list-inside">
          <li>
            <strong className="text-text-base">Voided match</strong> — if you paid for a Boost
            (75 ⭐) or Insurance (90 ⭐) on a fixture that is subsequently abandoned with
            insufficient minutes played per the competition's rules, your Stars are refunded as
            in-app credit.
          </li>
          <li>
            <strong className="text-text-base">Service unavailable</strong> — if a paid feature is
            unavailable due to our error for an extended period (more than 24 hours), contact
            us at <a className="text-trophy-gold" href="mailto:hello@punditxi.app">hello@punditxi.app</a>{' '}
            for a credit.
          </li>
        </ul>
        <p className="text-sm text-text-dim leading-relaxed mt-2">
          Other purchases are non-refundable. Bracket Premium is non-refundable once purchased;
          your bracket entry is locked at WC kickoff regardless of subsequent activity. Tipster
          Pack boosts remain in your inventory until used.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">7. Acceptable use</h2>
        <p className="text-sm text-text-dim leading-relaxed mb-2">
          You agree not to:
        </p>
        <ul className="space-y-2 text-sm text-text-dim list-disc list-inside">
          <li>Automate predictions or use bots to play on your behalf</li>
          <li>Operate multiple accounts or coordinate with others to manipulate leaderboards</li>
          <li>Attempt to reverse-engineer, exploit, or attack our systems</li>
          <li>Use PunditXI to harass, defame, or threaten other users</li>
          <li>Use the service for any unlawful purpose</li>
        </ul>
        <p className="text-sm text-text-dim leading-relaxed mt-2">
          Accounts violating these terms may be suspended without notice. Stars purchases on a
          suspended account are forfeit; in-app credit is also forfeit. Points and leaderboard
          rankings may be voided.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">8. Predictions are entertainment</h2>
        <p className="text-sm text-text-dim leading-relaxed">
          PunditXI is for entertainment. Nothing in the app — fixture odds, hints, leaderboards,
          or any other content — is financial advice or a tip for real-world betting. We are
          not a licensed gambling operator. If you have concerns about your gambling,{' '}
          <a className="text-trophy-gold" href="https://www.gamcare.org.uk" target="_blank" rel="noreferrer">
            GamCare
          </a>{' '}
          provides free support in the UK.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">9. Match data</h2>
        <p className="text-sm text-text-dim leading-relaxed">
          Fixture schedules, results, and player data are sourced from third-party data
          providers and may contain errors. We score predictions in good faith using the data
          we receive. If a match result is later corrected by the official source, we will
          re-score affected predictions and adjust points accordingly.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">10. Intellectual property</h2>
        <p className="text-sm text-text-dim leading-relaxed">
          The PunditXI brand, code, design, and content are owned by us. The predictions you
          make remain yours; you grant us a non-exclusive licence to display them on
          leaderboards and use them in aggregated, anonymous form to improve the service.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">11. Disclaimers</h2>
        <p className="text-sm text-text-dim leading-relaxed">
          The service is provided "as is" and "as available." We make no warranty that the
          service will be uninterrupted, error-free, or that any specific feature will continue
          to be supported. Football fixtures get rescheduled, results get corrected, third-party
          APIs go down — we do our best, but cannot guarantee perfection.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">12. Limitation of liability</h2>
        <p className="text-sm text-text-dim leading-relaxed">
          To the maximum extent permitted by law, our total liability to you arising out of or
          relating to PunditXI is limited to the total Stars amount you paid us in the 30 days
          before the event giving rise to the claim. We are not liable for indirect,
          consequential, or punitive damages. Nothing in these terms limits liability for fraud
          or for anything that cannot lawfully be limited under English law.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">13. Termination</h2>
        <p className="text-sm text-text-dim leading-relaxed">
          You may stop using PunditXI at any time. You can request account deletion by emailing{' '}
          <a className="text-trophy-gold" href="mailto:privacy@punditxi.app">
            privacy@punditxi.app
          </a>
          . We may suspend or terminate accounts for material violations of these terms, with or
          without notice depending on severity.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">14. Changes</h2>
        <p className="text-sm text-text-dim leading-relaxed">
          We may update these terms. For material changes (anything affecting your rights or
          payments), we'll notify you in-app or via a one-off bot message at least 14 days in
          advance. Continued use after the effective date counts as acceptance. The latest
          version is always at{' '}
          <a className="text-trophy-gold" href="https://punditxi.app/terms">
            punditxi.app/terms
          </a>
          .
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">15. Governing law</h2>
        <p className="text-sm text-text-dim leading-relaxed">
          These terms are governed by the laws of England and Wales. Disputes are subject to the
          exclusive jurisdiction of the English courts. If you're a consumer resident in the EU,
          you may also have the right to bring proceedings in the courts of your country of
          residence.
        </p>
      </Card>

      <Card>
        <h2 className="font-chunky text-base mb-2">16. Contact</h2>
        <p className="text-sm text-text-dim leading-relaxed">
          General questions:{' '}
          <a className="text-trophy-gold" href="mailto:hello@punditxi.app">
            hello@punditxi.app
          </a>
          <br />
          Privacy matters:{' '}
          <a className="text-trophy-gold" href="mailto:privacy@punditxi.app">
            privacy@punditxi.app
          </a>
        </p>
      </Card>
    </div>
  );
}
